import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  UserGroupIcon,
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useAuth } from '@/hooks/useAuth'
import {
  getAttendance,
  getAttendanceStats,
  type AttendanceRecord,
  type AttendanceStats,
} from '@/api/attendanceApi'
import { getGrades, getStreams, type Grade, type Stream } from '@/api/academicApi'
import ExportModal from '@/components/ui/ExportModal'
import { type ExportConfig } from '@/utils/exportUtils'
import toast from 'react-hot-toast'

const COLORS = ['#10B981', '#EF4444', '#F59E0B', '#2563EB', '#8B5CF6', '#EC4899']

interface DailyAttendance {
  date: string
  present: number
  absent: number
  late: number
  total: number
  rate: number
}

const AttendanceReportsPage: React.FC = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<AttendanceStats | null>(null)
  const [records, setRecords] = useState<AttendanceRecord[]>([])
  const [dailyData, setDailyData] = useState<DailyAttendance[]>([])
  const [grades, setGrades] = useState<Grade[]>([])
  const [streams, setStreams] = useState<Stream[]>([])
  const [showExportModal, setShowExportModal] = useState(false)

  // Filters
  const [startDate, setStartDate] = useState(() => {
    const date = new Date()
    date.setMonth(date.getMonth() - 1)
    return date.toISOString().split('T')[0]
  })
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0])
  const [selectedGrade, setSelectedGrade] = useState('')
  const [selectedStream, setSelectedStream] = useState('')

  useEffect(() => {
    fetchGrades()
  }, [])

  useEffect(() => {
    if (selectedGrade) {
      fetchStreams(selectedGrade)
    } else {
      setStreams([])
      setSelectedStream('')
    }
  }, [selectedGrade])

  useEffect(() => {
    fetchData()
  }, [startDate, endDate, selectedStream])

  const fetchGrades = async () => {
    try {
      const response = await getGrades({ limit: 100 })
      setGrades(response.grades || [])
    } catch (error) {
      console.error('Failed to fetch grades:', error)
    }
  }

  const fetchStreams = async (gradeId: string) => {
    try {
      const response = await getStreams({ gradeId })
      setStreams(response || [])
    } catch (error) {
      console.error('Failed to fetch streams:', error)
    }
  }

  const fetchData = async () => {
    try {
      setLoading(true)

      const params: any = {
        dateFrom: startDate,
        dateTo: endDate,
        limit: 1000,
      }

      if (selectedStream) {
        params.streamId = selectedStream
      }

      const [statsRes, attendanceRes] = await Promise.all([
        getAttendanceStats(params).catch(() => null),
        getAttendance(params).catch(() => null),
      ])

      if (statsRes?.data) {
        setStats(statsRes.data)
      }

      if (attendanceRes?.data) {
        const attendanceRecords = attendanceRes.data.attendance || attendanceRes.data.attendanceRecords || []
        setRecords(attendanceRecords)
        processDailyData(attendanceRecords)
      }
    } catch (error) {
      console.error('Failed to fetch attendance data:', error)
      toast.error('Failed to load attendance data')
    } finally {
      setLoading(false)
    }
  }

  const processDailyData = (records: AttendanceRecord[]) => {
    const dailyMap: Record<string, { present: number; absent: number; late: number; total: number }> = {}

    records.forEach((record) => {
      const date = record.sessionDate?.split('T')[0] || record.createdAt?.split('T')[0]
      if (!date) return

      if (!dailyMap[date]) {
        dailyMap[date] = { present: 0, absent: 0, late: 0, total: 0 }
      }

      const status = record.attendanceStatus || record.status
      if (status === 'present') dailyMap[date].present++
      else if (status === 'absent') dailyMap[date].absent++
      else if (status === 'late') dailyMap[date].late++
      dailyMap[date].total++
    })

    const daily: DailyAttendance[] = Object.entries(dailyMap)
      .map(([date, data]) => ({
        date,
        ...data,
        rate: data.total > 0 ? ((data.present + data.late) / data.total) * 100 : 0,
      }))
      .sort((a, b) => a.date.localeCompare(b.date))

    setDailyData(daily)
  }

  const getExportConfig = (): ExportConfig => {
    const selectedGradeName = grades.find(g => g._id === selectedGrade)?.name
    const selectedStreamName = streams.find(s => s._id === selectedStream)?.name
    const totalRecords = stats?.totalAttendanceRecords || stats?.totalAttendance || 0

    return {
      title: 'Attendance Report',
      subtitle: selectedGradeName
        ? `${selectedGradeName}${selectedStreamName ? ` - ${selectedStreamName}` : ''}`
        : 'All Grades & Streams',
      filename: `attendance-report-${startDate}-to-${endDate}`,
      institution: {
        name: 'SmartTag School',
      },
      dateRange: {
        from: startDate,
        to: endDate,
      },
      generatedAt: new Date().toLocaleString(),
      summary: [
        { label: 'Total Records', value: totalRecords },
        { label: 'Present', value: stats?.presentCount || 0 },
        { label: 'Absent', value: stats?.absentCount || 0 },
        { label: 'Late', value: stats?.lateCount || 0 },
        { label: 'Attendance Rate', value: `${stats?.attendanceRate?.toFixed(1) || 0}%` },
        { label: 'Avg Hours', value: `${stats?.avgHours?.toFixed(1) || 0}h` },
      ],
      sections: [
        {
          title: 'Attendance Summary',
          columns: [
            { header: 'Status', key: 'status', width: 25 },
            { header: 'Count', key: 'count', width: 20 },
            { header: 'Percentage', key: 'percentage', width: 20 },
          ],
          data: [
            {
              status: 'Present',
              count: stats?.presentCount || 0,
              percentage: totalRecords > 0
                ? `${((stats?.presentCount || 0) / totalRecords * 100).toFixed(1)}%`
                : '0%',
            },
            {
              status: 'Absent',
              count: stats?.absentCount || 0,
              percentage: totalRecords > 0
                ? `${((stats?.absentCount || 0) / totalRecords * 100).toFixed(1)}%`
                : '0%',
            },
            {
              status: 'Late',
              count: stats?.lateCount || 0,
              percentage: totalRecords > 0
                ? `${((stats?.lateCount || 0) / totalRecords * 100).toFixed(1)}%`
                : '0%',
            },
          ],
        },
        {
          title: 'Hours & Metrics',
          columns: [
            { header: 'Metric', key: 'metric', width: 35 },
            { header: 'Value', key: 'value', width: 20 },
          ],
          data: [
            { metric: 'Total Hours', value: `${stats?.totalHours?.toFixed(1) || 0}h` },
            { metric: 'Average Hours', value: `${stats?.avgHours?.toFixed(1) || 0}h` },
            { metric: 'Terminals Used', value: stats?.terminalCount || stats?.terminalsUsed?.length || 0 },
            { metric: 'Attendance Rate', value: `${stats?.attendanceRate?.toFixed(1) || 0}%` },
          ],
        },
        {
          title: 'Daily Breakdown',
          columns: [
            { header: 'Date', key: 'date', width: 25 },
            { header: 'Present', key: 'present', width: 15 },
            { header: 'Absent', key: 'absent', width: 15 },
            { header: 'Late', key: 'late', width: 15 },
            { header: 'Total', key: 'total', width: 15 },
            { header: 'Rate (%)', key: 'rate', width: 15 },
          ],
          data: dailyData.map(day => ({
            date: new Date(day.date).toLocaleDateString(),
            present: day.present,
            absent: day.absent,
            late: day.late,
            total: day.total,
            rate: `${day.rate.toFixed(1)}%`,
          })),
        },
        {
          title: 'Check-in Methods',
          columns: [
            { header: 'Method', key: 'method', width: 30 },
            { header: 'Count', key: 'count', width: 20 },
          ],
          data: stats?.checkInMethods
            ? Object.entries(stats.checkInMethods).map(([method, count]) => ({
                method: method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                count,
              }))
            : [],
        },
      ],
    }
  }

  // Prepare chart data
  const attendanceDistributionData = stats ? [
    { name: 'Present', value: stats.presentCount, color: '#10B981' },
    { name: 'Absent', value: stats.absentCount, color: '#EF4444' },
    { name: 'Late', value: stats.lateCount, color: '#F59E0B' },
  ].filter(item => item.value > 0) : []

  const checkInMethodsData = stats?.checkInMethods
    ? Object.entries(stats.checkInMethods).map(([method, count], index) => ({
        name: method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: count,
        color: COLORS[index % COLORS.length],
      }))
    : []

  const totalRecords = stats?.totalAttendanceRecords || stats?.totalAttendance || 0

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance Reports</h1>
          <p className="text-gray-600 mt-1">Detailed attendance analytics and insights</p>
        </div>
        <button
          onClick={() => setShowExportModal(true)}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
          Export Report
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm text-gray-600">Filters:</span>
          </div>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <span className="text-gray-400">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <select
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Grades</option>
            {grades.map((grade) => (
              <option key={grade._id} value={grade._id}>
                {grade.name}
              </option>
            ))}
          </select>
          {streams.length > 0 && (
            <select
              value={selectedStream}
              onChange={(e) => setSelectedStream(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Streams</option>
              {streams.map((stream) => (
                <option key={stream._id} value={stream._id}>
                  {stream.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-600">Total Records</p>
              <p className="text-xl font-bold text-gray-900">{totalRecords}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-600">Present</p>
              <p className="text-xl font-bold text-green-600">{stats?.presentCount || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircleIcon className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-gray-600">Absent</p>
              <p className="text-xl font-bold text-red-600">{stats?.absentCount || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-gray-600">Late</p>
              <p className="text-xl font-bold text-amber-600">{stats?.lateCount || 0}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <ChartBarIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-600">Attendance Rate</p>
              <p className="text-xl font-bold text-purple-600">{stats?.attendanceRate?.toFixed(1) || 0}%</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <ClockIcon className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs text-gray-600">Avg Hours</p>
              <p className="text-xl font-bold text-indigo-600">{stats?.avgHours?.toFixed(1) || 0}h</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Attendance Trend</h3>
          {dailyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                <YAxis />
                <Tooltip
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value: number, name: string) => [value, name.charAt(0).toUpperCase() + name.slice(1)]}
                />
                <Legend />
                <Area type="monotone" dataKey="present" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.6} name="Present" />
                <Area type="monotone" dataKey="late" stackId="1" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.6} name="Late" />
                <Area type="monotone" dataKey="absent" stackId="1" stroke="#EF4444" fill="#EF4444" fillOpacity={0.6} name="Absent" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              No attendance data available for the selected period
            </div>
          )}
        </motion.div>

        {/* Attendance Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Attendance Distribution</h3>
          {attendanceDistributionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={attendanceDistributionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {attendanceDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              No attendance data available
            </div>
          )}
        </motion.div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Rate Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Attendance Rate Over Time</h3>
          {dailyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                <Tooltip
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value: number) => [`${value.toFixed(1)}%`, 'Attendance Rate']}
                />
                <Line type="monotone" dataKey="rate" stroke="#2563EB" strokeWidth={2} dot={{ fill: '#2563EB' }} name="Rate" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              No data available for rate trend
            </div>
          )}
        </motion.div>

        {/* Check-in Methods */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Check-in Methods</h3>
          {checkInMethodsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={checkInMethodsData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {checkInMethodsData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-gray-500">
              No check-in method data available
            </div>
          )}
        </motion.div>
      </div>

      {/* Summary Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
        className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl shadow-sm p-6 text-white"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold">Attendance Summary</h3>
            <p className="text-primary-100 mt-1">
              {startDate} to {endDate}
            </p>
          </div>
          <div className="flex flex-wrap gap-6">
            <div className="text-center">
              <p className="text-primary-100 text-sm">Total Hours</p>
              <p className="text-2xl font-bold">{stats?.totalHours?.toFixed(0) || 0}h</p>
            </div>
            <div className="text-center">
              <p className="text-primary-100 text-sm">Terminals Used</p>
              <p className="text-2xl font-bold">{stats?.terminalCount || stats?.terminalsUsed?.length || 0}</p>
            </div>
            <div className="text-center">
              <p className="text-primary-100 text-sm">Overall Rate</p>
              <p className="text-2xl font-bold flex items-center gap-1">
                {stats?.attendanceRate?.toFixed(1) || 0}%
                {(stats?.attendanceRate || 0) >= 90 ? (
                  <ArrowTrendingUpIcon className="h-5 w-5 text-green-300" />
                ) : (
                  <ArrowTrendingDownIcon className="h-5 w-5 text-red-300" />
                )}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        config={getExportConfig()}
        title="Export Attendance Report"
      />
    </div>
  )
}

export default AttendanceReportsPage
